import { map, Observable } from 'rxjs';

import { ArableAdviceService } from '@app/shared/services/arable-advice.service';
import { CropFieldWithIrrigation, RegisterIrrigation } from '@app/irrigations/models/models';
import { Injectable } from '@angular/core';
import {
  GetIrrigationsResponse, RegisterIrrigationResponse,
} from '@app/irrigations/models/api-models';
import {
  mapGetIrrigationResponse,
  mapToRegisterIrrigationRequest,
  mapRegisterIrrigationResponse,
} from '@app/irrigations/models/mappers';

/**
 * Service for managing irrigation data for crop fields.
 * Provides CRUD operations for irrigation via the Arable Advice Service API.
 */
@Injectable({ providedIn: 'root' })
export class IrrigationService extends ArableAdviceService {
  private readonly irrigationsEndpoint = 'irrigations';
  
  /**
   * Retrieves irrigation records for specified crop fields and year.
   * @param cropFieldIds Array of crop field IDs to fetch irrigations for
   * @param year The crop year to filter irrigations
   * @returns Observable of crop fields with their irrigation data
   */
  getIrrigations(cropFieldIds: readonly number[], year: number): Observable<CropFieldWithIrrigation[]> {
    const url = `${this.baseUrl}/${this.irrigationsEndpoint}`;
    return this.http
      .get<GetIrrigationsResponse>(url, { params: { cropFieldIds: [...cropFieldIds], year } })
      .pipe(map(mapGetIrrigationResponse));
  }

  /**
   * Registers a new irrigation record or updates an existing one.
   * @param registerIrrigation The irrigation data to register
   * @returns Observable of the updated crop field with irrigations, or undefined if not found
   */
  registerIrrigation(registerIrrigation: RegisterIrrigation): Observable<CropFieldWithIrrigation[]> {
    const url = `${this.baseUrl}/${this.irrigationsEndpoint}`;
    const request = mapToRegisterIrrigationRequest(registerIrrigation);
    return this.http.post<RegisterIrrigationResponse>(url, request)
      .pipe(map(mapRegisterIrrigationResponse));
  }

  /**
   * Deletes an irrigation record from a crop field.
   * @param cropFieldId The ID of the crop field
   * @param irrigationId The ID of the irrigation to delete
   * @param cropYear The crop year of the irrigation
   * @returns Observable that completes when deletion is successful
   */
  deleteIrrigation(cropFieldId: number, irrigationId: number, cropYear: number): Observable<void> {
    const url = `${this.baseUrl}/${this.irrigationsEndpoint}/${cropFieldId}/${cropYear}/${irrigationId}`;
    return this.http.delete<void>(url);
  }
}
